import { Box, Divider, Grid, LinearProgress, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import TabBar from 'src/App/Dashboards/Market/Components/Tabs/TabBar';
import { COLOR_PRIMARY_2 } from 'src/utils/colors';
import ComingSoon from './ComingSoon';
import CurrencyChangeMenu from 'src/App/Dashboards/Market/Components/CurrencyChangeMenu';
import { User } from 'src/interfaces/iuser';
import { MONTHLY_TOP_HOST_LIMIT, MONTHLY_BEST_MARKET_FINDER_LIMIT, MONTHLY_MARKET_DASHBOARD_EXPORT_LIMIT } from 'src/utils/const';
import { getUserRemainingTopHostUnlockCredits } from 'src/utils/functions';
import { useCanAccessScalePlan } from 'src/hooks/useFeatureAccess';

const CurrencyTab = ({ currency, setCurrency }: { currency: string; setCurrency: any }) => {
  return (
    <Box
      sx={{
        width: 'clamp(250px, 50%, 500px)',
        '& .MuiMenuItem-root': {
          pl: 0,
        },
      }}
    >
      <CurrencyChangeMenu
        currency={currency}
        setCurrency={setCurrency}
        fullWidth={true}
        handleDefaultCurrencyOnly={true}
      />
    </Box>
  );
};

const MyUsageTab = ({ user }: { user: User }) => {
  const isUserAPremiumUser = ['expert', 'scale'].includes(user.subscriptionPlan);
  const isScaleUser = useCanAccessScalePlan();

  // Top Host Credits
  const topHostCreditsRemaining = getUserRemainingTopHostUnlockCredits(user);
  const topHostCreditsLimit = MONTHLY_TOP_HOST_LIMIT[user.subscriptionPlan];
  const topHostCreditsUsed = topHostCreditsLimit - topHostCreditsRemaining;
  const topHostCreditsUsedPercentage = Math.min(
    Math.ceil((topHostCreditsUsed / topHostCreditsLimit) * 100),
    100
  );

  // Market Finder Credits
  const bestMarketFinderUsage = user.bestMarketFinderUsage || {};
  const currentYearMonthString = new Date().toISOString().slice(0, 7);
  const marketFinderCreditsLimit = MONTHLY_BEST_MARKET_FINDER_LIMIT[user.subscriptionPlan];
  const marketFinderCreditsUsed = bestMarketFinderUsage[currentYearMonthString] || 0;
  const marketFinderCreditsRemaining = Math.max(
    0,
    marketFinderCreditsLimit - marketFinderCreditsUsed
  );
  const marketFinderCreditsUsedPercentage = Math.min(
    Math.ceil((marketFinderCreditsUsed / marketFinderCreditsLimit) * 100),
    100
  );

  // Market Explorer Export Usage
  const marketExplorerExportLimitRowMonthly = 1000;
  const marketExplorerExportLimitRowTotal =
    user.marketExplorerRowDataExportUsage[currentYearMonthString] || 0;


  // Market export credits
  const marketExportCreditsLimit = MONTHLY_MARKET_DASHBOARD_EXPORT_LIMIT[user.subscriptionPlan];
  const marketExportCreditsUsed = user.marketDashboardExportUsage[currentYearMonthString] || 0;
  const marketExportCreditsRemaining = Math.max(
    0,
    marketExportCreditsLimit - marketExportCreditsUsed
  );
  const marketExportCreditsUsedPercentage = Math.min(
    Math.ceil((marketExportCreditsUsed / marketExportCreditsLimit) * 100),
    100
  );

  return (
    <Grid container>
      <Grid item xs={12} sx={{ mb: '10px' }}>
        <Typography variant="body1">
          Current Plan:{' '}
          <span style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
            {user.subscriptionPlan}
          </span>
        </Typography>
      </Grid>

      <Grid item xs={12} sx={{ my: '10px' }}>
        <Divider />
      </Grid>

      {!isUserAPremiumUser && (
        <Typography variant="body1">
          This feature is only available with Expert or Scale plans.
        </Typography>
      )}

      {isUserAPremiumUser && (
        <>
          <Grid item xs={12} sx={{ mb: '10px' }}>
            <Typography variant="h6" sx={{ color: COLOR_PRIMARY_2 }}>
              Top Property Manager Data Access
            </Typography>
          </Grid>

          <Grid item xs={12} sx={{ mb: '10px' }}>
            <Typography variant="body1">
              Total Top Property Manager Data Access Limit:{' '}
              <span style={{ fontWeight: 'bold' }}>{topHostCreditsLimit}</span>
            </Typography>
          </Grid>

          <Grid item xs={12} sx={{ mb: '10px' }}>
            <Typography variant="body1">
              Total Top Property Manager Data Accessed:{' '}
              <span style={{ fontWeight: 'bold' }}>{topHostCreditsUsed}</span>
            </Typography>
          </Grid>

          <Grid item xs={12} sx={{ mb: '10px' }}>
            <Typography variant="body1">
              Total Top Property Manager Data Access Remaining:{' '}
              <span style={{ fontWeight: 'bold' }}>{topHostCreditsRemaining}</span>
            </Typography>
          </Grid>

          <Grid container item xs={12} alignItems={'center'} sx={{ mt: '10px' }}>
            <LinearProgress
              color="primary"
              variant="determinate"
              value={topHostCreditsUsedPercentage}
              sx={{ width: 'clamp(200px, 50%, 300px)', height: '10px', borderRadius: '2px' }}
            />
            <Typography
              variant="body1"
              sx={{ ml: '10px' }}
            >{`${topHostCreditsUsedPercentage}% used`}</Typography>
          </Grid>

          <Grid item xs={12} sx={{ my: '10px' }}>
            <Divider />
          </Grid>

          {/* Market Finder Usage */}
          <Grid item xs={12} sx={{ mb: '10px' }}>
            <Typography variant="h6" sx={{ color: COLOR_PRIMARY_2 }}>
              Market Explorer Search
            </Typography>
          </Grid>

          <Grid item xs={12} sx={{ mb: '10px' }}>
            <Typography variant="body1">
              Market Explorer Search Limit (Monthly):{' '}
              <span style={{ fontWeight: 'bold' }}>{marketFinderCreditsLimit}</span>
            </Typography>
          </Grid>

          <Grid item xs={12} sx={{ mb: '10px' }}>
            <Typography variant="body1">
              Market Explorer Searches Used (This Month):{' '}
              <span style={{ fontWeight: 'bold' }}>{marketFinderCreditsUsed}</span>
            </Typography>
          </Grid>

          <Grid item xs={12} sx={{ mb: '10px' }}>
            <Typography variant="body1">
              Market Explorer Searches Remaining (This Month):{' '}
              <span style={{ fontWeight: 'bold' }}>{marketFinderCreditsRemaining}</span>
            </Typography>
          </Grid>

          <Grid container item xs={12} alignItems={'center'} sx={{ mt: '10px' }}>
            <LinearProgress
              color="primary"
              variant="determinate"
              value={marketFinderCreditsUsedPercentage}
              sx={{ width: 'clamp(200px, 50%, 300px)', height: '10px', borderRadius: '2px' }}
            />
            <Typography
              variant="body1"
              sx={{ ml: '10px' }}
            >{`${marketFinderCreditsUsedPercentage}% used`}</Typography>
          </Grid>
          {/* End Market Finder Usage */}

          <Grid item xs={12} sx={{ my: '10px' }}>
            <Divider />
          </Grid>

          {/* Market Explorer Export Usage */}
          {isScaleUser && (
            <>
              <Grid item xs={12} sx={{ mb: '10px' }}>
                <Typography variant="h6" sx={{ color: COLOR_PRIMARY_2 }}>
                  Market Explorer Data Export
                </Typography>
              </Grid>

              <Grid item xs={12} sx={{ mb: '10px' }}>
                <Typography variant="body1">
                  Total Market Explorer Data Row Export Limit:{' '}
                  <span style={{ fontWeight: 'bold' }}>{marketExplorerExportLimitRowMonthly}</span>
                </Typography>
              </Grid>

              <Grid item xs={12} sx={{ mb: '10px' }}>
                <Typography variant="body1">
                  Total Market Explorer Row Exported:{' '}
                  <span style={{ fontWeight: 'bold' }}>{marketExplorerExportLimitRowTotal}</span>
                </Typography>
              </Grid>

              <Grid item xs={12} sx={{ mb: '10px' }}>
                <Typography variant="body1">
                  Market Explorer Rows Export Remaining (This Month):{' '}
                  <span style={{ fontWeight: 'bold' }}>
                    {marketExplorerExportLimitRowMonthly - marketExplorerExportLimitRowTotal}
                  </span>
                </Typography>
              </Grid>

              <Grid container item xs={12} alignItems={'center'} sx={{ mt: '10px' }}>
                <LinearProgress
                  color="primary"
                  variant="determinate"
                  value={
                    (marketExplorerExportLimitRowTotal / marketExplorerExportLimitRowMonthly) * 100
                  }
                  sx={{ width: 'clamp(200px, 50%, 300px)', height: '10px', borderRadius: '2px' }}
                />
                <Typography variant="body1" sx={{ ml: '10px' }}>{`${Math.min(
                  Math.ceil(
                    (marketExplorerExportLimitRowTotal / marketExplorerExportLimitRowMonthly) * 100
                  ),
                  100
                )}% used`}</Typography>
              </Grid>

              <Grid item xs={12} sx={{ my: '10px' }}>
                <Divider />
              </Grid>              

            </>
          )}

          {/* Market Export Credits */}
          <Grid item xs={12} sx={{ mb: '10px' }}>
                <Typography variant="h6" sx={{ color: COLOR_PRIMARY_2 }}>
                  Market Dashboard Export
                </Typography>
              </Grid>
              
              <Grid item xs={12} sx={{ mb: '10px' }}>
                <Typography variant="body1">
                  Total Market Dashboard Export Limit:{' '}
                  <span style={{ fontWeight: 'bold' }}>{marketExportCreditsLimit}</span>
                </Typography>
              </Grid>
              
              <Grid item xs={12} sx={{ mb: '10px' }}>
                <Typography variant="body1">
                  Total Market Dashboard Exported:{' '}
                  <span style={{ fontWeight: 'bold' }}>{marketExportCreditsUsed}</span>
                </Typography>
              </Grid>
              
              <Grid item xs={12} sx={{ mb: '10px' }}>
                <Typography variant="body1">
                  Market Dashboard Export Remaining (This Month):{' '}
                  <span style={{ fontWeight: 'bold' }}>{marketExportCreditsRemaining}</span>
                </Typography>
              </Grid>
              
              <Grid container item xs={12} alignItems={'center'} sx={{ mt: '10px' }}>
                <LinearProgress
                  color="primary"
                  variant="determinate"
                  value={marketExportCreditsUsedPercentage}
                  sx={{ width: 'clamp(200px, 50%, 300px)', height: '10px', borderRadius: '2px' }}
                />
                <Typography variant="body1" sx={{ ml: '10px' }}>{`${marketExportCreditsUsedPercentage}% used`}</Typography>
              </Grid>
              
              <Grid item xs={12} sx={{ my: '10px' }}>
                <Divider />
              </Grid>
        </>
      )}
    </Grid>
  );
};

const ProductSettingsTab = ({
  user,
  currency,
  setCurrency,
  OverviewLandingPageComponent,
  EstimatorDashboardComponent,
  isMobileScreen,
}: {
  user: User;
  currency: string;
  setCurrency: any;
  OverviewLandingPageComponent: JSX.Element;
  EstimatorDashboardComponent: JSX.Element;
  isMobileScreen: boolean;
}) => {
  const [tabSelected, setTabSelected] = useState<string>('currency');

  const tabsList = [
    {
      label: 'Currency',
      value: 'currency',
      onclick: () => {
        setTabSelected('currency');
      },
      component: <CurrencyTab currency={currency} setCurrency={setCurrency} />,
    },
    {
      label: 'Saved Markets',
      value: 'saved_markets',
      onclick: () => {
        setTabSelected('saved_markets');
      },
      component: OverviewLandingPageComponent,
    },
    {
      label: 'Saved Reports',
      value: 'saved_reports',
      onclick: () => {
        setTabSelected('saved_reports');
      },
      component: EstimatorDashboardComponent,
    },
    {
      label: 'My Usage',
      value: 'my_usage',
      onclick: () => {
        setTabSelected('my_usage');
      },
      component: <MyUsageTab user={user} />,
    },
    {
      label: 'Saved Filters',
      value: 'saved_filters',
      onclick: () => {
        setTabSelected('saved_filters');
      },
      component: <ComingSoon />,
    },
    {
      label: 'Data Export History',
      value: 'data_export_history',
      onclick: () => {
        setTabSelected('data_export_history');
      },
      component: <ComingSoon />,
    },
  ];

  return (
    <>
      <TabBar
        tabList={tabsList}
        selectedCurrentTab={tabSelected}
        color={COLOR_PRIMARY_2}
        renderLabelCondition={() => true}
        actionButton={<></>}
        showActionButton={false}
        isMobile={isMobileScreen}
      />
      <Box sx={{ py: '10px', position: 'relative', width: '100%' }}>
        {tabsList.map(tab => {
          if (tab.value === tabSelected) {
            return tab.component;
          }
          return <></>;
        })}
      </Box>
    </>
  );
};

export default ProductSettingsTab;
